import React from 'react';
import { Brain, Calculator, Users, Building2, Globe } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров расчета
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  institutionTypes,
  aiIntegrationLevels,
  regionFactors
}) => {
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения количества студентов
  const handleStudentsCountChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      studentsCount: newValue
    }));
  };
  
  // Обработчик изменения количества преподавателей
  const handleFacultyCountChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      facultyCount: newValue
    }));
  };
  
  // Обработчик изменения периода внедрения
  const handleImplementationPeriodChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      implementationPeriod: newValue
    }));
  };
  
  // Функция для быстрой настройки типа учреждения
  const setInstitutionType = (typeId) => {
    const selectedType = institutionTypes.find(type => type.id === typeId);
    if (selectedType) {
      setFormData(prev => ({
        ...prev,
        institutionType: typeId,
        studentsCount: selectedType.defaultStudents.toString(),
        facultyCount: selectedType.defaultFaculty.toString()
      }));
    }
  };
  
  // Функция для быстрой настройки уровня интеграции ИИ
  const setAiIntegrationLevel = (levelId) => {
    setFormData(prev => ({
      ...prev,
      aiIntegrationLevel: levelId
    }));
  };
  
  // Функция для быстрой настройки региона
  const setRegion = (regionId) => {
    const selectedRegion = regionFactors.find(region => region.id === regionId);
    if (selectedRegion) {
      setFormData(prev => ({
        ...prev,
        region: regionId,
        currency: selectedRegion.currency
      }));
    }
  };
  
  // Функция расчета
  const calculateCost = () => {
    // Здесь мы просто имитируем нажатие кнопки расчета в главной форме
    document.querySelector('button[type="submit"]')?.click();
  };
  
  // Получение текущих значений для отображения
  const numStudentsCount = parseIntOrDefault(formData.studentsCount, 0);
  const numFacultyCount = parseIntOrDefault(formData.facultyCount, 0);
  const implementationPeriod = parseIntOrDefault(formData.implementationPeriod, 12);
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-indigo-500" />
        Быстрая настройка параметров
      </h3>
      
      <div className="space-y-5">
        {/* Тип учебного заведения */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Тип учреждения</label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {institutionTypes.slice(0, 4).map((type) => (
              <button
                key={type.id}
                type="button"
                onClick={() => setInstitutionType(type.id)}
                className={`p-2 text-sm rounded-lg border transition-colors
                  ${formData.institutionType === type.id 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                <div className="font-medium flex items-center justify-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5" />
                  {type.name}
                </div>
              </button>
            ))}
          </div>
        </div>
        
        {/* Количество студентов */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="studentsCountSlider" className="text-gray-600 dark:text-gray-400">Количество студентов</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatNumber(numStudentsCount, 0)}</span>
          </div>
          <input
            type="range"
            id="studentsCountSlider"
            min="50"
            max="20000"
            step="50"
            value={numStudentsCount}
            onChange={handleStudentsCountChange}
            aria-label="Количество студентов"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Предопределенные значения */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {['500', '1000', '5000', '10000'].map(value => (
              <button
                key={value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, studentsCount: value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.studentsCount === value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {value}
              </button>
            ))}
          </div>
        </div>
        
        {/* Количество преподавателей */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="facultyCountSlider" className="text-gray-600 dark:text-gray-400">Количество преподавателей</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatNumber(numFacultyCount, 0)}</span>
          </div>
          <input
            type="range"
            id="facultyCountSlider"
            min="5"
            max="1000"
            step="5"
            value={numFacultyCount}
            onChange={handleFacultyCountChange}
            aria-label="Количество преподавателей"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Предопределенные значения */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {['30', '80', '150', '300'].map(value => (
              <button
                key={value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, facultyCount: value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.facultyCount === value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {value}
              </button>
            ))}
          </div>
        </div>
        
        {/* Период внедрения */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="implementationPeriodSlider" className="text-gray-600 dark:text-gray-400">Период внедрения (мес.)</label>
            <span className="font-medium text-gray-900 dark:text-white">{implementationPeriod}</span>
          </div>
          <input
            type="range"
            id="implementationPeriodSlider"
            min="3"
            max="36"
            step="1"
            value={implementationPeriod}
            onChange={handleImplementationPeriodChange}
            aria-label="Период внедрения (месяцев)"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Предопределенные значения */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {['6', '12', '18', '24'].map(value => (
              <button
                key={value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, implementationPeriod: value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.implementationPeriod === value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {value}
              </button>
            ))}
          </div>
        </div>
        
        {/* Уровень интеграции ИИ */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Уровень интеграции ИИ</label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {aiIntegrationLevels.slice(0, 4).map((level) => (
              <button
                key={level.id}
                type="button"
                onClick={() => setAiIntegrationLevel(level.id)}
                className={`p-2 text-sm rounded-lg border transition-colors
                  ${formData.aiIntegrationLevel === level.id 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                <div className="font-medium flex items-center justify-center gap-1.5">
                  <Brain className="w-3.5 h-3.5" />
                  {level.name}
                </div>
              </button>
            ))}
          </div>
        </div>
        
        {/* Регион */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Регион внедрения</label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {regionFactors.slice(0, 4).map((region) => (
              <button
                key={region.id}
                type="button"
                onClick={() => setRegion(region.id)}
                className={`p-2 text-sm rounded-lg border transition-colors
                  ${formData.region === region.id 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                <div className="font-medium flex items-center justify-center gap-1.5">
                  <Globe className="w-3.5 h-3.5" />
                  {region.name}
                </div>
              </button>
            ))}
          </div>
        </div>
        
        {/* Кнопка расчета */}
        <Button
          primary
          fullWidth
          onClick={calculateCost}
          icon={<Calculator className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать стоимость
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;